import { type FC } from 'react';
import { NumberInput, RadioButton, RadioButtonGroup } from '@carbon/react';
import { NumberField } from 'components/form-fields-2/number-field';
import styles from './create-area-panel.module.scss';
import {
  BOUNDARY_STYLES,
  CATALOG_BOUNDARY_STYLES,
  type BoundaryStyle,
} from './create-area-types';

interface BoundaryStyleSectionProps {
  style: BoundaryStyle;
  bufferFt: string;
  onStyleChange: (style: BoundaryStyle) => void;
  onBufferChange: (value: string) => void;
  /** Which styles to offer, in their fixed order. Defaults to the catalog's two. */
  offered?: readonly BoundaryStyle[];
  /** Replaces the bounding style's description, e.g. to name the selected wells. */
  boundingDescription?: string;
  /**
   * The buffer as Map Query's mock draws it for points: a small stepper with a
   * helper line. Absent, the catalog's grouped text field.
   */
  bufferStepper?: BufferStepper;
}

/** Whole feet, grouped by thousands. */
const BUFFER_FORMAT: Intl.NumberFormatOptions = { maximumFractionDigits: 0 };

/** Map Query's buffer stepper: its step and its helper line. */
export interface BufferStepper {
  step: number;
  helperText: string;
}

/**
 * From-selected: how the selected rows combine. The buffer applies to the
 * bounding hull only, so it appears with that choice.
 *
 * @param {BoundaryStyleSectionProps} props - The component props.
 * @return {JSX.Element} The boundary style controls.
 */
export const BoundaryStyleSection: FC<BoundaryStyleSectionProps> = ({
  style,
  bufferFt,
  onStyleChange,
  onBufferChange,
  offered = CATALOG_BOUNDARY_STYLES,
  boundingDescription,
  bufferStepper,
}) => (
  <section className={styles.section}>
    <RadioButtonGroup
      name="boundary-style"
      legendText="Boundary style"
      orientation="vertical"
      valueSelected={style}
      onChange={(value: string | number | undefined) =>
        onStyleChange(String(value) as BoundaryStyle)
      }
    >
      {BOUNDARY_STYLES.filter(({ id }) => offered.includes(id)).map(
        ({ id, title, description }) => (
          <RadioButton
            key={id}
            id={`boundary-style-${id}`}
            value={id}
            labelText={
              <span className={styles.optionLabel}>
                <span>{title}</span>
                <span className={styles.optionDescription}>
                  {id === 'bounding' && boundingDescription
                    ? boundingDescription
                    : description}
                </span>
              </span>
            }
          />
        )
      )}
    </RadioButtonGroup>

    {style === 'bounding' && bufferStepper && (
      // Text mode groups thousands (2,530), as the catalog's field does. No
      // ceiling: the backend takes any buffer, and a cap only turned the field
      // red with nothing to say why.
      <NumberInput
        id="new-area-buffer"
        label="Buffer (ft)"
        size="sm"
        type="text"
        inputMode="numeric"
        formatOptions={BUFFER_FORMAT}
        min={0}
        step={bufferStepper.step}
        allowEmpty
        helperText={bufferStepper.helperText}
        iconDescription="Buffer (ft)"
        value={bufferFt === '' ? '' : Number(bufferFt)}
        onChange={(_event: unknown, data: { value: number | string }) =>
          onBufferChange(data.value === '' ? '' : String(data.value))
        }
      />
    )}

    {style === 'bounding' && !bufferStepper && (
      // The shared number field, for the thousand separators asked for in UAT
      // 13: it groups as you type and keeps the caret where you left it, which
      // formatting the value by hand does not.
      <NumberField
        id="new-area-buffer"
        label="Buffer (ft)"
        value={bufferFt === '' ? null : Number(bufferFt)}
        onChange={(value) =>
          onBufferChange(value === null ? '' : String(value))
        }
        placeholder="0"
        thousandSeparator
        decimalScale={0}
        allowNegative={false}
      />
    )}
  </section>
);
